import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { message } from 'antd';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);

  // 初始化时检查认证状态
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking authentication status...');

        // 1. 检查localStorage中是否有token
        const storedToken = localStorage.getItem('token');

        // 2. 检查URL参数中是否有token
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        const loginSuccess = params.get('loginSuccess');

        // 3. 检查localStorage中是否有微信登录数据
        let wxLoginData = null;
        try {
          const wxLoginDataStr = localStorage.getItem('wx_login_data');
          if (wxLoginDataStr) {
            wxLoginData = JSON.parse(wxLoginDataStr);
            localStorage.removeItem('wx_login_data'); // 使用后立即删除
            console.log('Found WeChat login data in localStorage:', wxLoginData);
          }
        } catch (e) {
          console.error('Failed to parse wx_login_data:', e);
        }

        // 清除URL参数
        if (urlToken && loginSuccess) {
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
        }

        // 按优先级使用不同来源的登录信息
        if (urlToken && loginSuccess === 'true') {
          console.log('Using token from URL parameters');
          await fetchUserInfo(urlToken);
        }
        else if (wxLoginData && wxLoginData.token && wxLoginData.user) {
          console.log('Using WeChat login data from localStorage');
          setToken(wxLoginData.token);
          setUser(wxLoginData.user);
          localStorage.setItem('token', wxLoginData.token);
          api.defaults.headers.common['Authorization'] = `Bearer ${wxLoginData.token}`;
        }
        else if (storedToken) {
          console.log('Using token from localStorage');
          await fetchUserInfo(storedToken);
        }
        else {
          console.log('No authentication data found');
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 获取用户信息
  const fetchUserInfo = async (token) => {
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      localStorage.setItem('token', token);

      console.log('Fetching user info with token:', token);

      // 尝试获取用户信息
      try {
        const response = await api.get('/user/info');
        console.log('User info response:', response.data);

        if (response.data.success) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          return true;
        }
      } catch (apiError) {
        console.error('API error when fetching user info:', apiError);

        // 如果API调用失败，尝试从localStorage获取用户信息
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            console.log('Using stored user info:', parsedUser);
            setUser(parsedUser);
            return true;
          } catch (parseError) {
            console.error('Failed to parse stored user:', parseError);
          }
        }

        // 如果没有存储的用户信息，尝试从token解析
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));

          const payload = JSON.parse(jsonPayload);
          console.log('Token payload:', payload);

          if (payload.userId) {
            // 使用最小用户信息
            const minimalUser = {
              id: payload.userId,
              type: payload.type || 'wechat',
              nickname: '用户' + payload.userId.substring(0, 6)
            };

            console.log('Using minimal user info from token:', minimalUser);
            setUser(minimalUser);
            localStorage.setItem('user', JSON.stringify(minimalUser));
            return true;
          }
        } catch (tokenError) {
          console.error('Failed to parse token:', tokenError);
        }
      }

      // 如果所有方法都失败，返回false
      return false;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      return false;
    }
  };

  const login = async (emailOrToken, passwordOrUser) => {
    try {
      console.log('Login called with:', { emailOrToken, passwordOrUser });

      // 如果第一个参数是token，第二个参数是user对象，则直接设置登录状态
      if (typeof passwordOrUser === 'object' && passwordOrUser !== null) {
        console.log('Direct login with token and user');
        setToken(emailOrToken);
        setUser(passwordOrUser);
        localStorage.setItem('token', emailOrToken);
        localStorage.setItem('user', JSON.stringify(passwordOrUser));
        api.defaults.headers.common['Authorization'] = `Bearer ${emailOrToken}`;
        return { success: true };
      }

      // 否则，调用登录API
      console.log('Calling login API with email/password');
      const response = await api.post('/auth/login', {
        email: emailOrToken,
        password: passwordOrUser
      });

      console.log('Login API response:', response.data);

      if (response.data.success) {
        const { token, user } = response.data;

        // 确保token和user都存在
        if (!token || !user) {
          console.error('Login API returned success but missing token or user data');
          return { success: false, message: '登录成功但缺少必要数据' };
        }

        // 设置状态和localStorage
        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // 验证登录状态是否正确设置
        console.log('Login successful, state updated:', { token, user });
        return { success: true };
      }

      return { success: false, message: response.data.message || '登录失败' };
    } catch (error) {
      console.error('Login error:', error);

      // 提供更详细的错误信息
      let errorMessage = '登录失败';
      if (error.response) {
        // 服务器返回了错误响应
        errorMessage = error.response.data?.message || `服务器错误: ${error.response.status}`;
        console.error('Server error response:', error.response.data);
      } else if (error.request) {
        // 请求已发送但没有收到响应
        errorMessage = '服务器无响应，请检查网络连接';
      } else {
        // 请求设置时出现问题
        errorMessage = error.message || '请求错误';
      }

      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    message.success('已退出登录');
  };

  const register = async (email, password, username) => {
    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        username
      });
      if (response.data.success) {
        return response.data.user;
      }
      throw new Error(response.data?.message || '注册失败');
    } catch (error) {
      console.error('注册失败:', error);
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await api.put('/api/auth/profile', updates);
      if (response.data.success) {
        setUser(response.data.data);
        return response.data.data;
      }
      throw new Error(response.data?.message || '更新失败');
    } catch (error) {
      console.error('更新个人资料失败:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    updateProfile,
    fetchUserInfo,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;